import React, { useState, useEffect, useCallback } from 'react';
import * as API from '../api';
import { FONT, TEXT, TEXT2, TEXT3, BLUE, BLUE_LIGHT, BORDER, SUCCESS, WARNING, DANGER } from '../shared';
import { SkeletonList } from './ui/Skeleton';

const GLASS_CARD = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
  backdropFilter: "blur(34px) saturate(180%)",
  WebkitBackdropFilter: "blur(34px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.40)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.55), 0 6px 18px -10px rgba(11,107,211,0.18)",
};

const PLATFORM_COLOR = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  TikTok: "#010101",
  LinkedIn: "#0A66C2",
  Google: "#4285F4",
  YouTube: "#FF0000",
};

const STATUS_STYLE = {
  scheduled:  { bg: "#FEF9C3", color: "#B45309", label: "Scheduled" },
  published:  { bg: "#DCFCE7", color: "#16A34A", label: "Published" },
  posted:     { bg: "#DCFCE7", color: "#16A34A", label: "Posted" },
  active:     { bg: "#DBEAFE", color: "#1D4ED8", label: "Active" },
  paused:     { bg: "#FEF3C7", color: "#D97706", label: "Paused" },
  ended:      { bg: "#F1F5F9", color: "#64748B", label: "Ended" },
  draft:      { bg: "#F1F5F9", color: "#64748B", label: "Draft" },
  cancelled:  { bg: "#FEE2E2", color: "#DC2626", label: "Cancelled" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status?.toLowerCase()] || STATUS_STYLE.draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: s.bg, color: s.color, borderRadius: 6, padding: "2px 7px", flexShrink: 0 }}>
      {s.label}
    </span>
  );
}

function PlatformIcon({ platform, size = 14 }) {
  const color = PLATFORM_COLOR[platform] || TEXT2;
  if (platform === "Instagram") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill={color} stroke="none"/>
    </svg>
  );
  if (platform === "TikTok") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.15 8.15 0 004.77 1.52V6.72a4.85 4.85 0 01-1-.03z"/></svg>
  );
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
    </svg>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = d - now;
  const abs = Math.abs(diff);
  if (abs < 60_000) return "just now";
  if (abs < 3_600_000) return `${Math.round(abs / 60_000)}m ${diff > 0 ? "from now" : "ago"}`;
  if (abs < 86_400_000) return `${Math.round(abs / 3_600_000)}h ${diff > 0 ? "from now" : "ago"}`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtCurrency(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

// ─── Post Schedule Tab ───────────────────────────────────────────────────────

function PostRow({ post, onCancel, onPostNow }) {
  const [expanded, setExpanded] = useState(false);
  const status = (post.status || post.postStatus || "scheduled").toLowerCase();

  return (
    <div style={{ ...GLASS_CARD, borderRadius: 14, overflow: "hidden", marginBottom: 10 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
      >
        {/* Platform dot */}
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${PLATFORM_COLOR[post.platform] || BLUE}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <PlatformIcon platform={post.platform} size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: PLATFORM_COLOR[post.platform] || TEXT2 }}>{post.platform || "Platform"}</span>
            {post.postType && <span style={{ fontSize: 10, color: TEXT3 }}>· {post.postType}</span>}
            <StatusBadge status={status} />
          </div>
          <p style={{ fontSize: 13, color: TEXT, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {post.caption || post.description || "No caption"}
          </p>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: status === "scheduled" ? WARNING : TEXT2, margin: 0 }}>
            {fmtDate(post.scheduledAt || post.scheduled_at || post.createdAt)}
          </p>
        </div>

        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.4)", padding: "12px 16px" }}>
          {post.caption && (
            <p style={{ fontSize: 13, color: TEXT, lineHeight: 1.6, margin: "0 0 10px", whiteSpace: "pre-wrap" }}>{post.caption}</p>
          )}
          {post.hashtags && (
            <p style={{ fontSize: 12, color: BLUE, margin: "0 0 12px" }}>
              {Array.isArray(post.hashtags) ? post.hashtags.join(" ") : post.hashtags}
            </p>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {status === "scheduled" && onPostNow && (
              <button
                type="button"
                onClick={() => onPostNow(post)}
                style={{ padding: "7px 14px", borderRadius: 10, border: "none", background: `linear-gradient(135deg, #38BDF8, ${BLUE})`, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}
              >
                Post Now
              </button>
            )}
            {status === "scheduled" && onCancel && (
              <button
                type="button"
                onClick={() => onCancel(post)}
                style={{ padding: "7px 14px", borderRadius: 10, border: `1px solid rgba(239,68,68,0.3)`, background: "rgba(239,68,68,0.06)", color: DANGER, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PostScheduleTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    API.getScheduledPosts().then(data => {
      const arr = data?.posts || data?.schedules || (Array.isArray(data) ? data : []);
      setPosts(arr);
      setLoading(false);
    }).catch(() => { setError("Could not load schedule."); setLoading(false); });
  }, []);

  const handleCancel = useCallback(async (post) => {
    if (!window.confirm("Cancel this scheduled post?")) return;
    setCancelling(post.id);
    try {
      await API.cancelScheduledPost(post.id);
      setPosts(prev => prev.filter(p => p.id !== post.id));
    } catch {
      alert("Could not cancel post.");
    } finally {
      setCancelling(null);
    }
  }, []);

  const handlePostNow = useCallback(async (post) => {
    if (!window.confirm("Post this now?")) return;
    try {
      await API.publishPost({ platform: post.platform, caption: post.caption, hashtags: post.hashtags, image: post.imageUrl });
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, status: "published" } : p));
    } catch {
      alert("Could not post now.");
    }
  }, []);

  if (loading) return <SkeletonList count={5} />;
  if (error) return <ErrorMsg msg={error} />;
  if (posts.length === 0) return <EmptyState icon="calendar" message="No scheduled posts yet." hint="Ask Vella to schedule a post in Vella Relay." />;

  const upcoming = posts.filter(p => ["scheduled", "pending"].includes((p.status || "").toLowerCase()));
  const past = posts.filter(p => !["scheduled", "pending"].includes((p.status || "").toLowerCase()));

  return (
    <div>
      {upcoming.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>Upcoming ({upcoming.length})</p>
          {upcoming.map((p, i) => <PostRow key={p.id || i} post={p} onCancel={handleCancel} onPostNow={handlePostNow} />)}
        </>
      )}
      {past.length > 0 && (
        <div style={{ marginTop: upcoming.length > 0 ? 24 : 0 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>Past ({past.length})</p>
          {past.map((p, i) => <PostRow key={p.id || i} post={p} />)}
        </div>
      )}
    </div>
  );
}

// ─── Ad Schedule Tab ─────────────────────────────────────────────────────────

function CampaignRow({ campaign }) {
  const [expanded, setExpanded] = useState(false);
  const status = (campaign.status || "active").toLowerCase();

  return (
    <div style={{ ...GLASS_CARD, borderRadius: 14, overflow: "hidden", marginBottom: 10 }}>
      <div
        onClick={() => setExpanded(e => !e)}
        style={{ padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
      >
        <div style={{ width: 36, height: 36, borderRadius: 10, background: `${PLATFORM_COLOR[campaign.platform] || BLUE}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <PlatformIcon platform={campaign.platform} size={18} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: PLATFORM_COLOR[campaign.platform] || TEXT2 }}>{campaign.platform || "Platform"}</span>
            <StatusBadge status={status} />
          </div>
          <p style={{ fontSize: 13, color: TEXT, margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {campaign.name || campaign.description || campaign.objective || "Ad Campaign"}
          </p>
        </div>

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: TEXT, margin: "0 0 1px" }}>{fmtCurrency(campaign.budget || campaign.spend)}</p>
          <p style={{ fontSize: 10, color: TEXT3, margin: 0 }}>budget</p>
        </div>

        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={TEXT3} strokeWidth="2" style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.4)", padding: "12px 16px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 8, marginBottom: 12 }}>
            {[
              { label: "Budget", val: fmtCurrency(campaign.budget) },
              { label: "Spend", val: fmtCurrency(campaign.spend) },
              { label: "Clicks", val: campaign.clicks?.toLocaleString() },
              { label: "Impressions", val: campaign.impressions?.toLocaleString() },
              { label: "ROAS", val: campaign.roas ? `${campaign.roas}x` : null },
              { label: "Target", val: campaign.targetAudience || campaign.audience },
            ].filter(m => m.val != null).map(m => (
              <div key={m.label} style={{ background: "rgba(255,255,255,0.55)", borderRadius: 10, padding: "8px 10px" }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: TEXT, margin: "0 0 2px" }}>{m.val}</p>
                <p style={{ fontSize: 10, color: TEXT3, margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>{m.label}</p>
              </div>
            ))}
          </div>

          {(campaign.startDate || campaign.start_date) && (
            <p style={{ fontSize: 11, color: TEXT3, margin: 0 }}>
              {fmtDate(campaign.startDate || campaign.start_date)} → {campaign.endDate || campaign.end_date ? fmtDate(campaign.endDate || campaign.end_date) : "ongoing"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AdScheduleTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.getCampaignAnalytics({}).then(data => {
      const arr = data?.campaigns || data?.data || (Array.isArray(data) ? data : []);
      setCampaigns(arr);
      setLoading(false);
    }).catch(() => { setError("Could not load campaigns."); setLoading(false); });
  }, []);

  if (loading) return <SkeletonList count={5} />;
  if (error) return <ErrorMsg msg={error} />;
  if (campaigns.length === 0) return <EmptyState icon="target" message="No ad campaigns yet." hint="Ask Vella to launch a campaign in Vella Relay." />;

  const active = campaigns.filter(c => ["active", "running"].includes((c.status || "").toLowerCase()));
  const inactive = campaigns.filter(c => !["active", "running"].includes((c.status || "").toLowerCase()));

  return (
    <div>
      {active.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>Active ({active.length})</p>
          {active.map((c, i) => <CampaignRow key={c.id || i} campaign={c} />)}
        </>
      )}
      {inactive.length > 0 && (
        <div style={{ marginTop: active.length > 0 ? 24 : 0 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 10px" }}>Inactive ({inactive.length})</p>
          {inactive.map((c, i) => <CampaignRow key={c.id || i} campaign={c} />)}
        </div>
      )}
    </div>
  );
}

// ─── Shared utilities ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
        <div style={{ width: 28, height: 28, border: `3px solid ${BLUE}`, borderTop: "3px solid transparent", borderRadius: "50%", animation: "spinSV 0.75s linear infinite" }} />
        <style>{`@keyframes spinSV { to { transform: rotate(360deg); } }`}</style>
        <p style={{ fontSize: 13, color: TEXT3, margin: 0 }}>Loading...</p>
      </div>
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 14, padding: 20, textAlign: "center" }}>
      <p style={{ color: DANGER, fontSize: 14, margin: 0 }}>{msg}</p>
    </div>
  );
}

function EmptyState({ icon, message, hint }) {
  const icons = {
    calendar: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
    target: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  };
  return (
    <div style={{ ...GLASS_CARD, borderRadius: 24, padding: "60px 40px", textAlign: "center" }}>
      <div style={{ width: 52, height: 52, borderRadius: 16, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
          <path d={icons[icon] || icons.calendar}/>
        </svg>
      </div>
      <p style={{ fontSize: 15, fontWeight: 600, color: TEXT, margin: "0 0 6px" }}>{message}</p>
      <p style={{ fontSize: 13, color: TEXT3, margin: 0 }}>{hint}</p>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function ScheduleView() {
  const [tab, setTab] = useState("posts");

  const TAB_BTN = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => setTab(id)}
      style={{
        padding: "8px 20px",
        borderRadius: 12,
        border: "none",
        background: tab === id ? "#fff" : "transparent",
        color: tab === id ? TEXT : TEXT3,
        fontSize: 13,
        fontWeight: tab === id ? 700 : 500,
        cursor: "pointer",
        fontFamily: FONT,
        boxShadow: tab === id ? "0 2px 8px -2px rgba(11,107,211,0.14), 0 1px 0 rgba(255,255,255,0.9) inset" : "none",
        transition: "all 0.2s ease",
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ fontFamily: FONT, maxWidth: 800, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Schedule</h2>
        <p style={{ fontSize: 13, color: TEXT3, margin: 0 }}>Upcoming posts and active ad campaigns.</p>
      </div>

      {/* Tab bar */}
      <div style={{ ...GLASS_CARD, borderRadius: 16, padding: 5, marginBottom: 24, display: "inline-flex", gap: 2 }}>
        {TAB_BTN("posts", "Post Schedule")}
        {TAB_BTN("ads", "Ad Schedule")}
      </div>

      {tab === "posts" ? <PostScheduleTab /> : <AdScheduleTab />}
    </div>
  );
}
